// src/widgets/ControlLightsWidget.tsx
import React, { useState, useRef, useEffect } from 'react';
import { LightingCanvas } from '../components/LightingCanvas';
import { Light, LightPosition, LightProperties, Room, DEFAULT_ROOMS } from '../types/Light';
import { calculateScaledDimensions, ScaledPosition } from '../types/Dimensions';
import { useTool } from '../contexts/ToolContext';
import './ControlLightsWidget.scss';

interface ControlLightsProps {
  className?: string;
}

// Define the rooms in the blueprint
const ROOMS: Room[] = DEFAULT_ROOMS;

export const ControlLightsWidget: React.FC<ControlLightsProps> = ({
  className,
}) => {

  const { lightDescriptions } = useTool()

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scaling, setScaling] = useState<ScaledPosition>({ x: 0, y: 0, scale: 1 });
  const [lights, setLights] = useState<Light[]>([]);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0 });
  const [infoMessage, setInfoMessage] = useState('')

  // Load image and set dimensions
  useEffect(() => {
    const img = new Image();
    img.src = '/showcase_room.jpg';
    img.onload = () => {
      setOriginalImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      // Trigger initial scaling calculation
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };
  }, []);

  // Update scaling when container size or image dimensions change
  useEffect(() => {
    if (!containerRef.current || !originalImageDimensions.width || !originalImageDimensions.height) return;

    const updateScaling = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      setContainerSize({
        width: rect.width,
        height: rect.height,
      });

      const newScaling = calculateScaledDimensions(
        rect.width,
        rect.height,
        originalImageDimensions.width,
        originalImageDimensions.height
      );
      setScaling(newScaling);
    };

    const resizeObserver = new ResizeObserver(updateScaling);
    resizeObserver.observe(containerRef.current);
    updateScaling(); // Initial scaling calculation

    return () => resizeObserver.disconnect();
  }, [originalImageDimensions]);

  const convertToImageCoordinates = (clientX: number, clientY: number): LightPosition => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };

    return {
      x: (clientX - rect.left - scaling.x) / scaling.scale,
      y: (clientY - rect.top - scaling.y) / scaling.scale,
    };
  };

  const findRoomForPosition = (position: LightPosition): Room | null => {
    return ROOMS.find(room => {
      const { bounds } = room;
      return (
        position.x >= bounds.x &&
        position.x <= bounds.x + bounds.width &&
        position.y >= bounds.y &&
        position.y <= bounds.y + bounds.height
      );
    }) || null;
  };

  useEffect(() => {

    const newLights: Light[] = []

    lightDescriptions.forEach(description => {
      const newLight = Light.fromDescription(description)
      if (newLight) newLights.push(newLight)
    })

    setLights(newLights)
  }, [lightDescriptions])

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const position = convertToImageCoordinates(e.clientX, e.clientY);

    if (
      position.x >= 0 &&
      position.x <= originalImageDimensions.width &&
      position.y >= 0 &&
      position.y <= originalImageDimensions.height
    ) {
      const room = findRoomForPosition(position);

      if (room) {
        const properties: LightProperties = {
          color: '#FFAA00',
          intensity: 0.4,
          radius: 200,
        };

        setLights(prevLights => [
          ...prevLights,
          new Light(position, properties, room.id)
        ]);
      }
    }
  };

  useEffect(() => {
    const length = lights.length
    if (length === 1) setInfoMessage('1 light is on.')
    else if (length > 1) setInfoMessage(`${length} lights are on.`)
    else setInfoMessage('Lights are off.')
  }, [lights])

  return (
    <div
      ref={containerRef}
      className={`control-lights-widget ${className || ''}`}
    >
      <div className="blueprint-container">
        {containerSize.width > 0 && containerSize.height > 0 && (
          <LightingCanvas
            width={containerSize.width}
            height={containerSize.height}
            lights={lights}
            rooms={ROOMS}
            scaling={scaling}
            originalImageDimensions={originalImageDimensions}
            backgroundSrc="/showcase_room.jpg"
          />
        )}
      </div>
      <div className="widget-title">control_lights()</div>
      <div className="widget-info">
        {infoMessage}
      </div>
    </div>
  );
};